import React, { useEffect, useState } from "react";
import {
  MdOutlineDashboard,
  MdOutlineDirectionsBike,
  MdOutlinePhotoSizeSelectActual,
  MdOutlineComment,
  MdOutlineEventAvailable,
  MdOutlineContactMail,
  MdOutlineSettings,
} from "react-icons/md";
import { FiLogOut, FiChevronRight, FiChevronDown } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";

 
const menuItems = [
  {
    name: "Dashboard",
    path: "/admin/dashboard",
    icon: MdOutlineDashboard,
    roles: ["admin"],
  },
  {
    name: "Motorcycles",
    path: "/admin/motorcycles",
    icon: MdOutlineDirectionsBike,
    roles: ["admin"],
  },
  {
    name: "Home Slider",
    path: "/admin/home-slides",
    icon: MdOutlinePhotoSizeSelectActual,
    roles: ["admin"],
  },
  {
    name: "Happy Customers",
    path: "/admin/testimonials",
    icon: MdOutlineComment,
    roles: ["admin"],
  },
  {
    name: "Test Rides",
    path: "/admin/bookings",
    icon: MdOutlineEventAvailable,
    roles: ["admin"],
  },
  {
    name: "Inquiries",
    path: "/admin/inquiries",
    icon: MdOutlineContactMail,
    roles: ["admin"],
  },
];

const Sidebar = ({ currentPath, isSidebarOpen, navigate, setIsSidebarOpen }) => {
  const { user, logout } = useAuth();
  const [settingsOpen, setSettingsOpen] = useState(currentPath.startsWith("/admin/settings"));


  const filteredMenu = menuItems.filter((item) =>
    !item.roles || item.roles.includes(user?.role)
  );

  useEffect(() => {
    const handleResize = () => setIsSidebarOpen(window.innerWidth >= 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [setIsSidebarOpen]);

  // Get initials from user name
  const initials = user?.name
    ? user.name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()
    : "AD";

  return (
    <>
      {/* ── Sidebar ── */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-screen w-64 flex flex-col
          bg-white border-r border-gray-100 shadow-xl
          transition-transform duration-300 ease-in-out
          ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}
          lg:translate-x-0
        `}
      >
        {/* ── Logo area ── */}
        <div className="flex flex-col items-center justify-center px-5 py-6 border-b border-gray-100 shrink-0">
          <span className="text-center text-lg font-black leading-tight text-slate-950">
            Kalikambal
          </span>
          <span className="text-center text-xs font-bold uppercase tracking-[0.25em] text-amber-600">
            Motors
          </span>
        </div>

        {/* ── User profile chip ── */}
        <div className="mx-4 mt-4 mb-2 px-3 py-3 rounded-2xl bg-indigo-50 flex items-center gap-3 shrink-0">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-400 text-white text-xs font-bold flex items-center justify-center shadow-sm shrink-0">
            {initials}
          </div>
          <div className="overflow-hidden">
            <p className="text-sm font-semibold text-gray-800 truncate leading-tight">
              {user?.name || "Admin"}
            </p>
            <p className="text-[11px] text-indigo-500 font-medium capitalize">{user?.role || "admin"}</p>
          </div>
        </div>

        {/* ── Section label ── */}
        <p className="px-6 pt-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-gray-400">
          Main Menu
        </p>

        {/* ── Navigation ── */}
        <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-0.5">
          {filteredMenu.map(({ name, path, icon: Icon }) => (
            <button
              key={name}
              type="button"
              onClick={() => {
                navigate(path);
                if (window.innerWidth < 1024) {
                  setIsSidebarOpen(false);
                }
              }}
              className={`
                group relative flex items-center gap-3 px-3 py-2.5 rounded-xl
                text-sm font-medium transition-all duration-150 w-full text-left
                ${currentPath === path
                  ? "bg-indigo-600 text-white shadow-md shadow-indigo-200"
                  : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"
                }
              `}
            >
              {currentPath === path && (
                <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r-full bg-white/40" />
              )}

              <Icon
                className={`text-[19px] shrink-0 transition-colors ${
                  currentPath === path ? "text-white" : "text-gray-400 group-hover:text-indigo-500"
                }`}
              />

              <span className="flex-1">{name}</span>

              {currentPath === path && (
                <FiChevronRight className="text-white/60 text-sm" />
              )}
            </button>
          ))}

          {/* Settings Section */}
          <div className="pt-2 border-t border-gray-100 mt-2">
            <button
              type="button"
              onClick={() => setSettingsOpen(!settingsOpen)}
              className={`
                group relative flex items-center gap-3 px-3 py-2.5 rounded-xl
                text-sm font-medium transition-all duration-150 w-full text-left
                ${currentPath.startsWith("/admin/settings")
                  ? "bg-slate-100 text-slate-900"
                  : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"
                }
              `}
            >
              <MdOutlineSettings className="text-[19px] shrink-0 text-gray-400 group-hover:text-indigo-500" />
              <span className="flex-1">Settings</span>
              {settingsOpen ? (
                <FiChevronDown className="text-gray-400 text-sm" />
              ) : (
                <FiChevronRight className="text-gray-400 text-sm" />
              )}
            </button>
            
            {settingsOpen && (
              <div className="pl-6 mt-1 space-y-0.5 animate-in slide-in-from-top-1 duration-150">
                <button
                  type="button"
                  onClick={() => {
                    navigate("/admin/settings/bike-models");
                    if (window.innerWidth < 1024) {
                      setIsSidebarOpen(false);
                    }
                  }}
                  className={`
                    flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold w-full text-left transition-all
                    ${currentPath === "/admin/settings/bike-models"
                      ? "text-indigo-600 bg-indigo-50"
                      : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"
                    }
                  `}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-current shrink-0" />
                  <span>Bike Models</span>
                </button>
              </div>
            )}
          </div>
        </nav>

        {/* ── Logout ── */}
        <div className="px-3 py-4 border-t border-gray-100 shrink-0">
          <button
            onClick={() => {
              logout();
              navigate("/admin/login");
            }}
            className="
              group flex items-center gap-3 w-full px-3 py-2.5 rounded-xl
              text-sm font-medium text-gray-500
              hover:bg-red-50 hover:text-red-500 transition-all
            "
          >
            <FiLogOut className="text-[17px] group-hover:text-red-500 transition-colors" />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* ── Mobile overlay ── */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden transition-opacity"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </>
  );
};

export default Sidebar;
